import { useState } from "react";
import { Check, Building2, Users, TrendingUp, Award, Send, Sparkles, MapPin, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { franchisePackages } from "../mock/mock";
import { submitFranchise } from "../services/api";
import FranchiseInfographic from "../components/infographics/FranchiseInfographic";

const FranchisePage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", city: "", investment: "", message: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitFranchise(form);
      toast({ title: "Enquiry received!", description: "Our franchise team will contact you within 48 hours." });
      setForm({ name: "", email: "", phone: "", city: "", investment: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Award, title: "Proven Curriculum", desc: "Unique Sanatan + Modern pedagogy backed by ECCE experts." },
    { icon: Users, title: "Training Support", desc: "Comprehensive teacher training and ongoing academic support." },
    { icon: TrendingUp, title: "Growing Market", desc: "India's early childhood segment is expected to reach ₹50,000 Cr by 2030." },
    { icon: Building2, title: "Turn-key Setup", desc: "End-to-end setup guidance including design, hiring and marketing." }
  ];

  return (
    <div>
      <section className="pt-16 pb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Franchise Opportunity</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Bring <span className="text-orange-600">Aadi Gayanam</span> to Your City
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Partner with us to build a purpose-driven play school in your community — where Sanatan values meet modern learning. Early partner enrolments now open.
          </p>
        </div>
      </section>

      <FranchiseInfographic />

      <section className="py-16 pattern-warm">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-stone-800 mb-3">
              Choose Your <span className="text-orange-600">Partnership Model</span>
            </h2>
            <p className="text-stone-600 max-w-xl mx-auto">Flexible franchise options designed for cities and communities of every size.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {franchisePackages.map((p) => (
              <div
                key={p.id}
                className={`relative ${p.color} rounded-3xl p-8 border-2 ${p.popular ? "border-orange-500 shadow-2xl" : "border-white shadow-lg"}`}
              >
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-600 text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                    Most Popular
                  </span>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl ${p.accent} flex items-center justify-center`}>
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display font-bold text-2xl text-stone-800">{p.name}</h3>
                </div>
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-widest mb-4">{p.tier}</p>

                <div className="border-y border-stone-200 py-4 mb-4 space-y-2">
                  <div>
                    <p className="text-xs text-stone-500">Investment</p>
                    <p className="font-display font-bold text-2xl text-stone-800">{p.setup}</p>
                  </div>
                  <div>
                    <p className="text-xs text-stone-500">Space Required</p>
                    <p className="font-semibold text-stone-800">{p.space}</p>
                  </div>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                      <div className={`w-5 h-5 rounded-full ${p.accent} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <Check className="w-3 h-3 text-white" />
                      </div>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button className={`w-full ${p.accent} hover:opacity-90 text-white rounded-full h-11`}>
                  Enquire Now
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-orange-50 to-amber-50 rounded-3xl p-8 md:p-10 border border-orange-100 shadow-xl">
            <div className="text-center mb-8">
              <h3 className="font-display text-2xl md:text-3xl font-bold text-stone-800 mb-2">Start Your Franchise Journey</h3>
              <p className="text-stone-600">Fill this form and our franchise team will reach out within 48 hours.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <Label className="text-stone-700 font-semibold">Full Name *</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">Phone *</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">Email *</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div>
                  <Label className="text-stone-700 font-semibold">City *</Label>
                  <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City & State" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-stone-700 font-semibold">Investment Capacity *</Label>
                  <Input required value={form.investment} onChange={(e) => setForm({ ...form, investment: e.target.value })} placeholder="e.g., ₹15-25 Lakhs" className="mt-1.5 bg-white h-11 border-orange-200" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-stone-700 font-semibold">Message</Label>
                  <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about yourself and your interest..." className="mt-1.5 bg-white border-orange-200 min-h-[100px]" />
                </div>
              </div>
              <Button type="submit" size="lg" disabled={loading} className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full h-12 shadow-lg btn-shine">
                {loading ? <><Loader2 className="mr-2 w-4 h-4 animate-spin" /> Submitting...</> : <>Submit Franchise Enquiry <Send className="ml-2 w-4 h-4" /></>}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FranchisePage;
